import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, DollarSign, Calendar, CheckCircle2, 
  Clock, AlertCircle, Download, CreditCard, X, Loader2 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { showToast } from '../utils/toast';
import { formatDateEST, formatCurrency as formatCurrencyEST } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientInvoices() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API}/api/client/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setClient(data);
        setInvoices(data.invoices || []);
        
        // Fetch billing summary
        if (data.client_id) {
          fetchBillingSummary(data.client_id);
        }
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingSummary = async (clientId) => {
    try {
      const response = await fetch(`${API}/api/admin/billing/summary/${clientId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingSummary(data);
      }
    } catch (error) {
      console.error('Error fetching billing summary:', error);
    }
  };

  const handlePayNow = async (invoice) => {
    if (!window.confirm(`Process payment of ${formatCurrency(invoice.total || invoice.amount)} for ${invoice.invoice_number}?`)) {
      return;
    }

    setPaying(true);

    try {
      // MOCK PAYMENT FLOW - Simulates Stripe payment success
      // In production: Redirect to Stripe Checkout or Payment Link
      
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate processing
      
      // Mock payment success - mark invoice as paid
      const response = await fetch(`${API}/api/admin/invoices/${invoice.invoice_id}/mark-paid`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        showToast({
          title: 'Payment Successful!',
          message: 'Your payment has been processed',
          type: 'success'
        });
        
        // Refresh data
        await fetchInvoices();
        setSelectedInvoice(null);
      } else {
        throw new Error('Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      showToast({
        title: 'Payment Failed',
        message: 'Could not process payment. Please try again.',
        type: 'error'
      });
    } finally {
      setPaying(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return formatDateEST(dateString);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      sent: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      viewed: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      paid: 'bg-green-500/20 text-green-300 border-green-500/30',
      overdue: 'bg-red-500/20 text-red-300 border-red-500/30',
      failed: 'bg-red-500/20 text-red-300 border-red-500/30',
      void: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };

    const icons = {
      draft: <FileText className="w-3 h-3" />,
      sent: <Clock className="w-3 h-3" />,
      viewed: <Clock className="w-3 h-3" />,
      paid: <CheckCircle2 className="w-3 h-3" />,
      overdue: <AlertCircle className="w-3 h-3" />,
      failed: <AlertCircle className="w-3 h-3" />,
      void: <X className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.sent}`}>
        {icons[status] || icons.sent}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10 bg-black/20 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/client-dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Invoices & Billing</h1>
              <p className="text-blue-200 text-sm">View your billing history and make payments</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Billing Summary Cards */}
        {billingSummary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
              <p className="text-blue-200 text-xs mb-1">Total Invoiced</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(billingSummary.total_invoiced)}</p>
            </div>
            <div className="bg-green-400/10 backdrop-blur-xl rounded-2xl p-4 border border-green-400/30">
              <p className="text-green-200 text-xs mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-green-300">{formatCurrency(billingSummary.total_paid)}</p>
            </div>
            <div className="bg-yellow-400/10 backdrop-blur-xl rounded-2xl p-4 border border-yellow-400/30">
              <p className="text-yellow-200 text-xs mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-yellow-300">{formatCurrency(billingSummary.outstanding_balance)}</p>
            </div>
            <div className="bg-red-400/10 backdrop-blur-xl rounded-2xl p-4 border border-red-400/30">
              <p className="text-red-200 text-xs mb-1">Overdue</p>
              <p className="text-2xl font-bold text-red-300">{formatCurrency(billingSummary.overdue_amount)}</p>
            </div>
          </div>
        )}

        {/* Invoices List */}
        <div className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">All Invoices</h2>
            <p className="text-blue-200 text-sm mt-1">{invoices.length} total invoices</p>
          </div>

          {invoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-blue-300 mx-auto mb-4 opacity-50" />
              <p className="text-white text-lg font-medium mb-2">No invoices yet</p>
              <p className="text-blue-200">Your invoices will appear here</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Invoice</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Description</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Due Date</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Status</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-blue-200 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {invoices.map((invoice) => (
                      <tr key={invoice.invoice_id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{invoice.invoice_number}</p>
                            <p className="text-blue-300 text-xs">{formatDate(invoice.created_at)}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-blue-100 text-sm">{invoice.description}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-white font-semibold">{formatCurrency(invoice.total || invoice.amount)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-blue-200 text-sm">{formatDate(invoice.due_date)}</p>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedInvoice(invoice)}
                            className="text-blue-300 hover:text-blue-200 hover:bg-white/10"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-white/10">
                {invoices.map((invoice) => (
                  <div key={invoice.invoice_id} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{invoice.invoice_number}</p>
                        <p className="text-blue-300 text-xs">{formatDate(invoice.created_at)}</p>
                      </div>
                      {getStatusBadge(invoice.status)}
                    </div>
                    <p className="text-blue-100 text-sm mb-2">{invoice.description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{formatCurrency(invoice.total || invoice.amount)}</p>
                        <p className="text-blue-300 text-xs">Due: {formatDate(invoice.due_date)}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setSelectedInvoice(invoice)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-slate-900/95 backdrop-blur-lg border-b border-white/10 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Invoice Details</h2>
                <p className="text-blue-200 text-sm">{selectedInvoice.invoice_number}</p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-blue-300 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Status & Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-blue-200 text-sm mb-1">Status</p>
                  {getStatusBadge(selectedInvoice.status)}
                </div>
                <div>
                  <p className="text-blue-200 text-sm mb-1">Amount</p>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency(selectedInvoice.total || selectedInvoice.amount)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-blue-200 text-sm mb-1">Invoice Date</p>
                  <p className="text-white">{formatDate(selectedInvoice.created_at)}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-sm mb-1">Due Date</p>
                  <p className="text-white">{formatDate(selectedInvoice.due_date)}</p>
                </div>
              </div>

              {selectedInvoice.paid_date && (
                <div>
                  <p className="text-blue-200 text-sm mb-1">Paid Date</p>
                  <p className="text-green-300">{formatDate(selectedInvoice.paid_date)}</p>
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-blue-200 text-sm mb-2">Description</p>
                <p className="text-white">{selectedInvoice.description}</p>
              </div>

              {/* Line Items */}
              {selectedInvoice.line_items && selectedInvoice.line_items.length > 0 && (
                <div>
                  <p className="text-blue-200 text-sm mb-3">Line Items</p>
                  <div className="bg-white/5 rounded-lg p-4 space-y-2">
                    {selectedInvoice.line_items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">{item.description}</p>
                          <p className="text-blue-300 text-xs">Qty: {item.quantity} × {formatCurrency(item.unit_price)}</p>
                        </div>
                        <p className="text-white font-medium">{formatCurrency(item.total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between text-blue-200 mb-2">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.subtotal || selectedInvoice.amount)}</span>
                </div>
                {selectedInvoice.tax > 0 && (
                  <div className="flex items-center justify-between text-blue-200 mb-2">
                    <span>Tax</span>
                    <span>{formatCurrency(selectedInvoice.tax)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-white font-bold text-lg">
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.total || selectedInvoice.amount)}</span>
                </div>
              </div>

              {/* Event History (Simple) */}
              <div>
                <p className="text-blue-200 text-sm mb-3">Activity</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <p className="text-blue-200">
                      Created on {formatDate(selectedInvoice.created_at)}
                    </p>
                  </div>
                  {selectedInvoice.sent_at && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <p className="text-blue-200">
                        Sent on {formatDate(selectedInvoice.sent_at)}
                      </p>
                    </div>
                  )}
                  {selectedInvoice.paid_date && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <p className="text-green-300">
                        Paid on {formatDate(selectedInvoice.paid_date)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                {selectedInvoice.pdf_url && (
                  <a
                    href={selectedInvoice.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/10"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </a>
                )}
                
                {['sent', 'overdue'].includes(selectedInvoice.status) && (
                  <Button
                    onClick={() => handlePayNow(selectedInvoice)}
                    disabled={paying}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                  >
                    {paying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay Now
                      </>
                    )}
                  </Button>
                )}
                
                {['paid', 'void', 'failed'].includes(selectedInvoice.status) && (
                  <Button
                    disabled
                    className="flex-1 bg-gray-600 text-gray-300 cursor-not-allowed"
                  >
                    {selectedInvoice.status === 'paid' ? 'Paid' : selectedInvoice.status === 'void' ? 'Voided' : 'Failed'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
